import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatFileSize, type FileAttachment } from '@avslack/shared';
import { apiFetch } from '../lib/api';
import { formatDay } from '../lib/format';
import { Dialog, useAnimatedClose } from './Dialog';
import { Icon } from './Icon';

interface FileEntry {
  file: FileAttachment;
  channelId: string;
  messageId: string;
  createdAt: string;
}

const KINDS = [
  { value: 'all', label: 'Mind' },
  { value: 'image', label: 'Képek' },
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Videó' },
  { value: 'other', label: 'Egyéb' },
] as const;

/**
 * Fajlbongeszo.
 *
 * A "hol is volt az a kep" kerdesre valaszol: tipus szerint szurve, es minden
 * tetelrol egy kattintassal az eredeti uzenethez lehet ugrani.
 */
export function FileBrowser({
  channelId,
  channelLabel,
  onClose: closeNow,
}: {
  channelId: string;
  channelLabel: string;
  onClose: () => void;
}) {
  const onClose = useAnimatedClose(closeNow);
  const [kind, setKind] = useState<(typeof KINDS)[number]['value']>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['files', channelId, kind],
    queryFn: () =>
      apiFetch<{ files: FileEntry[] }>(
        `/api/files?channelId=${channelId}&kind=${kind}&limit=120`,
      ).then((r) => r.files),
    staleTime: 30_000,
  });

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Fájlok"
      description={`A ${channelLabel} csatornába feltöltött fájlok.`}
      width="lg"
    >
      <div className="mt-4">
        <div className="mb-3 flex flex-wrap gap-1.5">
          {KINDS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setKind(option.value)}
              aria-pressed={kind === option.value}
              className={[
                'rounded-pill border px-3 py-1 text-xs transition-colors duration-(--duration-quick)',
                kind === option.value
                  ? 'border-accent bg-accent-soft font-semibold text-accent'
                  : 'border-border-soft text-ink-muted hover:border-border-strong',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>

        {isLoading && <p className="py-8 text-center text-sm text-ink-muted">Betöltés…</p>}

        {!isLoading && data?.length === 0 && (
          <div className="py-12 text-center">
            <div aria-hidden="true" className="mb-2 text-3xl opacity-40">
              <Icon name="csatolmany" />
            </div>
            <p className="text-sm text-ink-muted">Ebben a szűrésben nincs fájl.</p>
          </div>
        )}

        <ul className="max-h-[calc(var(--app-height,100dvh)*0.50)] space-y-1 overflow-y-auto overscroll-contain">
          {data?.map((entry) => (
            <li key={entry.file.id}>
              <Link
                to="/c/$channelId"
                params={{ channelId: entry.channelId }}
                search={{ m: entry.messageId }}
                onClick={onClose}
                className="flex items-center gap-3 rounded-control border border-border-soft px-3 py-2 transition-colors duration-(--duration-quick) hover:border-border-strong"
              >
                {entry.file.thumbUrl ? (
                  <img
                    src={entry.file.thumbUrl}
                    alt=""
                    loading="lazy"
                    width={40}
                    height={40}
                    className="size-10 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="grid size-10 shrink-0 place-items-center rounded bg-surface-sunken"
                  >
                    <Icon name={entry.file.mime === 'application/pdf' ? 'fajl' : 'csatolmany'} />
                  </span>
                )}

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{entry.file.name}</span>
                  <span className="block text-2xs text-ink-faint">
                    {formatFileSize(entry.file.size)} · {formatDay(entry.createdAt)}
                  </span>
                </span>

                <span aria-hidden="true" className="shrink-0 text-ink-faint">
                  <Icon name="nyilJobbra" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
